import { MpControl } from '@mp-react/form';
import { z } from 'zod';
import { BenefitCategory } from 'store/benefits';
import { ContentLanguage } from 'types/general';
import {
  dateRangeSchema,
  singleDateSchema,
  numberRangeSchema,
} from 'utils/schemas';
import { AdminPermission } from 'store/admin-admins';
import { AdminRole } from 'store/admins';
import { PermissionName } from 'store/permissions';
import {
  GlobalPermissionModules,
  LocalPermissionModules,
  PermissionLevels,
} from '../constants/Administrators';
import { StatusAction } from './Status';

type LinkModule =
  | LocalPermissionModules
  | GlobalPermissionModules
  | Array<LocalPermissionModules | GlobalPermissionModules>;

interface BaseLink {
  title: string;
  badge?: number;
  roles: AdminRole[];
  module?: LinkModule;
  permissionLevel?: PermissionLevels;
  matchAllModules?: boolean;
  /**
   * Permissions (v2) required to access the link. Access is granted when the
   * user's role has `accessLevel > 0` on at least one of the listed
   * permissions. Applies only when the `advancedPermissions` feature is on.
   */
  permission?: readonly PermissionName[];
}

interface OneLevelLink extends BaseLink {
  to: string;
}

interface TwoLevelsLink extends BaseLink {
  to?: string;
  links: OneLevelLink[];
}

export type LinkItem = OneLevelLink | TwoLevelsLink;

export interface PageItem {
  component: React.FC;
  path: string;
  pages?: PageItem[];
  public?: boolean;
  roles?: AdminRole[];
  module?:
    | LocalPermissionModules
    | GlobalPermissionModules
    | Array<LocalPermissionModules | GlobalPermissionModules>;
  adminModules?: AdminPermission[];
  permissionLevel?: PermissionLevels;
  /**
   * Permissions (v2) required to access the route. Access is granted when the
   * user's role has `accessLevel > 0` on at least one of the listed
   * permissions. Applies only when the `advancedPermissions` feature is on.
   */
  permission?: readonly PermissionName[];
}

export interface Status {
  name: StatusAction;
  date?: string;
  tz?: number;
}

export interface Timezone {
  value: number;
  label: string;
}

export interface TooltipConfig {
  text: string;
  width?: number;
}

export interface TooltipControl extends MpControl {
  tooltip?: string | TooltipConfig;
  sufleris?: string;
  children?: TooltipControl[];
}

export interface CustomFormControl extends TooltipControl {
  disabled?: boolean;
  readonly?: boolean;
  children?: CustomFormControl[];
  disablePast?: boolean;
  disableFuture?: boolean;
  props?: Record<string, any>;
}

export interface UseLoading {
  loading: boolean;
  stopLoading: () => void;
  startLoading: () => void;
}

export interface CheckAssignBaseResponse {
  totalInvestmentAmount: number;
  investmentAmountChange: number;
}

enum InvestmentTypes {
  employee = 'employee',
  benefits = 'benefits',
  groups = 'groups',
}

export interface Investment {
  id: string;
  name: string;
  added: boolean;
  type: InvestmentTypes;
  benefitType?: BenefitCategory;
  employeeCount?: number;
  investment: number;
}

export type NumberRange = z.infer<typeof numberRangeSchema>;
export type DateRange = z.infer<typeof dateRangeSchema>;

export type SingleDate = z.infer<typeof singleDateSchema>;

/**
 * Type for extracting array element type.
 */
export type ArrayElement<ArrayType extends readonly unknown[]> =
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

export interface KeyLabelPair<K extends string = string> {
  key: K;
  label: string;
}

export type Translations<T extends Record<string, any>> = Array<
  {
    language: Uppercase<ContentLanguage>;
  } & T
>;

export const binaries = ['yes', 'no'] as const;
export type Binary = (typeof binaries)[number];

const levels = ['low', 'medium', 'high'] as const;
export type Level = (typeof levels)[number];

// MUI performance issue with SxProps
// https://github.com/microsoft/TypeScript/issues/34801
export type Sx = any;
